import React, { useEffect, useState } from 'react';

// Pitch an Available deal to ONE buyer from your own Gmail (POST /api/gmail/deals/:id/pitch).
//
// One buyer per message on purpose: no buyer ever sees another's address, and a person presses Send
// each time — the bulk blast stays disabled. The buyer is chosen from the buyers you can see and is
// addressed from their record, never a typed address. The body is pre-filled from the deal's
// sales-safe fields only (price, specs, ARV, close date) — nothing here knows the contract price.

function fmtMoney(n) {
  if (n == null || n === '') return null;
  const v = Number(n);
  return Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : null;
}

function draftBody(deal, arv) {
  const where = [deal.address, [deal.city, deal.state].filter(Boolean).join(', '), deal.zip].filter(Boolean).join(' ');
  const specs = [deal.beds && `${deal.beds} bd`, deal.baths && `${deal.baths} ba`,
    deal.sqft && `${Number(deal.sqft).toLocaleString()} sqft`, deal.year_built && `built ${deal.year_built}`].filter(Boolean).join(' · ');
  const lines = [
    'Hi,',
    '',
    `I have a deal I think fits what you're buying: ${where}.`,
    '',
    specs && `• ${specs}`,
    fmtMoney(deal.marketing_price ?? deal.asking_price) && `• Price: ${fmtMoney(deal.marketing_price ?? deal.asking_price)}`,
    fmtMoney(arv) && `• ARV: ${fmtMoney(arv)}`,
    deal.close_date && `• Close: ${String(deal.close_date).slice(0, 10)}`,
    deal.photos_link && `• Photos: ${deal.photos_link}`,
    '',
    'Let me know if you want to see it.',
  ];
  return lines.filter(l => l !== null && l !== undefined && l !== false).join('\n');
}

export default function PitchBuyerModal({ deal, arv, onClose }) {
  const [buyers, setBuyers] = useState([]);
  const [q, setQ] = useState('');
  const [buyerId, setBuyerId] = useState('');
  const [subject, setSubject] = useState(`Deal: ${deal.address}`);
  const [body, setBody] = useState(() => draftBody(deal, arv));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch('/api/buyers?limit=5300', { credentials: 'include' }).then(r => (r.ok ? r.json() : []))
      .then(d => setBuyers((Array.isArray(d) ? d : (Array.isArray(d?.buyers) ? d.buyers : [])).filter(b => b.email)))
      .catch(() => {});
  }, []);

  const shown = q
    ? buyers.filter(b => `${b.name || ''} ${b.company || ''} ${b.email || ''}`.toLowerCase().includes(q.toLowerCase()))
    : buyers;
  const picked = buyers.find(b => String(b.id) === String(buyerId));

  async function send() {
    setSending(true); setError(null);
    try {
      const r = await fetch(`/api/gmail/deals/${deal.id}/pitch`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buyer_id: Number(buyerId), subject, body }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not send');
      setResult(d);
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Pitch to a buyer</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{deal.address} · sent from your Gmail, one buyer per email</div>
        </div>

        {result ? (
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 14, color: 'var(--white)', fontWeight: 600, marginBottom: 6 }}>
              {result.status === 'sent' ? `Sent to ${picked?.name || picked?.email}.` : 'Logged, not sent.'}
            </div>
            {result.status !== 'sent' && (
              <div style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>Sending is switched off for this workspace, so the email was recorded as simulated.</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => { setResult(null); setBuyerId(''); }} style={ghostBtn}>Pitch another buyer</button>
              <button onClick={onClose} style={primaryBtn}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: 20, maxHeight: '65vh', overflowY: 'auto' }}>
              <label style={labelStyle}>Buyer</label>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search your buyers…" style={{ ...inputStyle, marginBottom: 8 }} />
              <select value={buyerId} onChange={e => setBuyerId(e.target.value)} size={Math.min(6, Math.max(2, shown.length))} style={{ ...inputStyle, marginBottom: 16 }}>
                {shown.slice(0, 300).map(b => (
                  <option key={b.id} value={b.id}>{[b.name, b.company].filter(Boolean).join(' · ') || b.email} — {b.email}</option>
                ))}
              </select>
              {buyers.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: -8, marginBottom: 16 }}>
                  You have no buyers with an email yet. Add them in Buyers Database.
                </div>
              )}
              <label style={labelStyle}>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} style={{ ...inputStyle, marginBottom: 16 }} />
              <label style={labelStyle}>Message</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={11} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
              {error && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--gold)' }}>{error}</div>}
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={ghostBtn}>Cancel</button>
              <button onClick={send} disabled={!buyerId || !body.trim() || sending}
                style={{ ...primaryBtn, opacity: (!buyerId || !body.trim() || sending) ? 0.5 : 1, cursor: (!buyerId || sending) ? 'default' : 'pointer' }}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel      = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(560px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 14 };
const ghostBtn   = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const primaryBtn = { padding: '8px 18px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700 };
